import * as React from "react";
import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { useAuth } from "@/hooks/useAuth";

const LoginPage = React.lazy(async () => ({
  default: (await import("@/pages/Auth/LoginPage")).LoginPage
}));
const PhaseDetailPage = React.lazy(async () => ({
  default: (await import("@/pages/Phases/PhaseDetailPage")).PhaseDetailPage
}));
const TaskDetailPage = React.lazy(async () => ({
  default: (await import("@/pages/Tasks/TaskDetailPage")).TaskDetailPage
}));
const QuotesPage = React.lazy(async () => ({
  default: (await import("@/pages/Quotes/QuotesPage")).QuotesPage
}));
const QuoteDetailPage = React.lazy(async () => ({
  default: (await import("@/pages/Quotes/QuoteDetailPage")).QuoteDetailPage
}));
const DeadlinesPage = React.lazy(async () => ({
  default: (await import("@/pages/Deadlines/DeadlinesPage")).DeadlinesPage
}));
const ExpensesPage = React.lazy(async () => ({
  default: (await import("@/pages/Expenses/ExpensesPage")).ExpensesPage
}));
const SfrbBudgetPage = React.lazy(async () => ({
  default: (await import("@/pages/Expenses/SfrbBudgetPage")).SfrbBudgetPage
}));
const SfrbMatchPage = React.lazy(async () => ({
  default: (await import("@/pages/Expenses/SfrbMatchPage")).SfrbMatchPage
}));
const ContactsPage = React.lazy(async () => ({
  default: (await import("@/pages/Contacts/ContactsPage")).ContactsPage
}));
const BotPage = React.lazy(async () => ({
  default: (await import("@/pages/Bot/BotPage")).BotPage
}));
const ApprovalsPage = React.lazy(async () => ({
  default: (await import("@/pages/Approvals/ApprovalsPage")).ApprovalsPage
}));
const DataCleanupPage = React.lazy(async () => ({
  default: (await import("@/pages/Settings/DataCleanupPage")).DataCleanupPage
}));
const DphReviewPage = React.lazy(async () => ({
  default: (await import("@/pages/Expenses/DphReviewPage")).DphReviewPage
}));
const MissingReceiptsPage = React.lazy(async () => ({
  default: (await import("@/pages/Expenses/MissingReceiptsPage")).MissingReceiptsPage
}));
const BudgetPage = React.lazy(async () => ({
  default: (await import("@/pages/Budget/BudgetPage")).BudgetPage
}));
const CashflowPage = React.lazy(async () => ({
  default: (await import("@/pages/Cashflow/CashflowPage")).CashflowPage
}));
const TptSettlementPage = React.lazy(async () => ({
  default: (await import("@/pages/Expenses/TptSettlementPage")).TptSettlementPage
}));
const QuoteComparePage = React.lazy(async () => ({
  default: (await import("@/pages/Quotes/QuoteComparePage")).QuoteComparePage
}));
const ChangeOrdersPage = React.lazy(async () => ({
  default: (await import("@/pages/Money/ChangeOrdersPage")).ChangeOrdersPage
}));
const CommitmentsPage = React.lazy(async () => ({
  default: (await import("@/pages/Money/CommitmentsPage")).CommitmentsPage
}));
const KolaudaciaPage = React.lazy(async () => ({
  default: (await import("@/pages/Admin/KolaudaciaPage")).KolaudaciaPage
}));
const SupervisorPage = React.lazy(async () => ({
  default: (await import("@/pages/Admin/SupervisorPage")).SupervisorPage
}));
const PermitsPage = React.lazy(async () => ({
  default: (await import("@/pages/Admin/PermitsPage")).PermitsPage
}));
const InspectionsPage = React.lazy(async () => ({
  default: (await import("@/pages/Tasks/InspectionsPage")).InspectionsPage
}));
const DrawdownsPage = React.lazy(async () => ({
  default: (await import("@/pages/Admin/DrawdownsPage")).DrawdownsPage
}));
const MaPage = React.lazy(async () => ({
  default: (await import("@/pages/Ma/MaPage")).MaPage
}));
const MunkaPage = React.lazy(async () => ({
  default: (await import("@/pages/Munka/MunkaPage")).MunkaPage
}));
const DokumentumokHubPage = React.lazy(async () => ({
  default: (await import("@/pages/Dokumentumok/DokumentumokPage")).DokumentumokHubPage
}));
const AsszisztensPage = React.lazy(async () => ({
  default: (await import("@/pages/Asszisztens/AsszisztensPage")).AsszisztensPage
}));
const BeallitasokPage = React.lazy(async () => ({
  default: (await import("@/pages/Settings/BeallitasokPage")).BeallitasokPage
}));
const AuditLogPage = React.lazy(async () => ({
  default: (await import("@/pages/Settings/AuditLogPage")).AuditLogPage
}));
const BotApiKeysPage = React.lazy(async () => ({
  default: (await import("@/pages/Settings/BotApiKeysPage")).BotApiKeysPage
}));
const NotificationPrefsPage = React.lazy(async () => ({
  default: (await import("@/pages/Settings/NotificationPrefsPage")).NotificationPrefsPage
}));
const AnomaliesPage = React.lazy(async () => ({
  default: (await import("@/pages/Settings/AnomaliesPage")).AnomaliesPage
}));
const ShareLinksPage = React.lazy(async () => ({
  default: (await import("@/pages/Settings/ShareLinksPage")).ShareLinksPage
}));
const AiOpsPage = React.lazy(async () => ({
  default: (await import("@/pages/Settings/AiOpsPage")).AiOpsPage
}));
const ShareLinkPage = React.lazy(async () => ({
  default: (await import("@/pages/Share/ShareLinkPage")).ShareLinkPage
}));
const DeliveriesPage = React.lazy(async () => ({
  default: (await import("@/pages/Deliveries/DeliveriesPage")).DeliveriesPage
}));
const DepositsPage = React.lazy(async () => ({
  default: (await import("@/pages/Money/DepositsPage")).DepositsPage
}));
const InvoicesPage = React.lazy(async () => ({
  default: (await import("@/pages/Money/InvoicesPage")).InvoicesPage
}));
const WarrantiesPage = React.lazy(async () => ({
  default: (await import("@/pages/Money/WarrantiesPage")).WarrantiesPage
}));
const PayerBalancePage = React.lazy(async () => ({
  default: (await import("@/pages/Money/PayerBalancePage")).PayerBalancePage
}));
const PlanningPage = React.lazy(async () => ({
  default: (await import("@/pages/Money/PlanningPage")).PlanningPage
}));
const RatingsPage = React.lazy(async () => ({
  default: (await import("@/pages/Money/RatingsPage")).RatingsPage
}));
const BoqLinksPage = React.lazy(async () => ({
  default: (await import("@/pages/Money/BoqLinksPage")).BoqLinksPage
}));
const RfqPage = React.lazy(async () => ({
  default: (await import("@/pages/Quotes/RfqPage")).RfqPage
}));

function PageFallback() {
  return (
    <div className="min-h-dvh bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50">
      <div className="mx-auto flex max-w-md items-center justify-center px-4 py-10">
        <LoadingSpinner />
      </div>
    </div>
  );
}

/** Cut features: soft landing back to a useful hub. */
function GoneTo({ to, label }: { to: string; label: string }) {
  return <Navigate to={to} replace state={{ notice: label }} />;
}

function QuoteDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/penz/ajanlatok/${id}`} replace />;
}

function CompareRedirect() {
  const { variantGroup } = useParams();
  return <Navigate to={`/penz/osszehasonlitas/${variantGroup ?? "bricks"}`} replace />;
}

export function App() {
  return (
    <React.Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Navigate to="/ma" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/share/:token" element={<ShareLinkPage />} />

        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            {/* Primary hubs */}
            <Route path="/ma" element={<MaPage />} />
            <Route path="/atvetelek" element={<DeliveriesPage />} />
            <Route path="/munka" element={<MunkaPage />} />
            <Route path="/penz" element={<Navigate to="/penz/kiadasok" replace />} />
            <Route path="/penz/kiadasok" element={<ExpensesPage />} />
            <Route path="/penz/rozpocet" element={<SfrbBudgetPage />} />
            <Route path="/penz/rozpocet/parositas" element={<SfrbMatchPage />} />
            <Route path="/penz/rozpocet/parositas/:expenseId" element={<SfrbMatchPage />} />
            <Route path="/penz/ajanlatok" element={<QuotesPage />} />
            <Route path="/penz/ajanlatok/:id" element={<QuoteDetailPage />} />
            <Route path="/penz/osszehasonlitas" element={<Navigate to="/penz/osszehasonlitas/bricks" replace />} />
            <Route path="/penz/osszehasonlitas/:variantGroup" element={<QuoteComparePage />} />
            <Route path="/penz/lekotesek" element={<CommitmentsPage />} />
            <Route path="/penz/valtozasok" element={<ChangeOrdersPage />} />
            <Route path="/penz/cashflow" element={<CashflowPage />} />
            <Route path="/penz/elszamolas" element={<TptSettlementPage />} />
            <Route path="/penz/szamlak" element={<InvoicesPage />} />
            <Route path="/penz/letetek" element={<DepositsPage />} />
            <Route path="/penz/garancia" element={<WarrantiesPage />} />
            <Route path="/penz/egyenleg" element={<PayerBalancePage />} />
            <Route path="/penz/tervezes" element={<PlanningPage />} />
            <Route path="/penz/ertekeles" element={<RatingsPage />} />
            <Route path="/penz/boq" element={<BoqLinksPage />} />
            <Route path="/penz/rfq" element={<RfqPage />} />
            <Route path="/dokumentumok" element={<DokumentumokHubPage />} />
            <Route path="/asszisztens" element={<AsszisztensPage />} />
            <Route path="/beallitasok" element={<BeallitasokPage />} />

            {/* Deep links kept — fázislista → Munka (összevonva) */}
            <Route path="/phases" element={<Navigate to="/munka" replace />} />
            <Route path="/phases/:phaseId" element={<PhaseDetailPage />} />
            <Route path="/phases/:phaseId/tasks/:taskId" element={<TaskDetailPage />} />
            <Route path="/ajanlatok/:id" element={<QuoteDetailPage />} />
            <Route path="/kiadasok/dph-ellenorzes" element={<DphReviewPage />} />
            <Route path="/kiadasok/tpt-elszamolas" element={<Navigate to="/penz/elszamolas" replace />} />
            <Route path="/kiadasok/bizonylat-hiany" element={<MissingReceiptsPage />} />
            <Route path="/budget" element={<BudgetPage />} />
            <Route path="/lehivasok" element={<DrawdownsPage />} />
            <Route path="/kolaudacio" element={<KolaudaciaPage />} />
            <Route path="/dozor" element={<SupervisorPage />} />
            <Route path="/engedelyek" element={<PermitsPage />} />
            <Route path="/ellenorzesek" element={<InspectionsPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/hataridok" element={<DeadlinesPage />} />
            <Route path="/bot" element={<BotPage />} />
            <Route path="/beallitasok/adattisztitas" element={<DataCleanupPage />} />
            <Route path="/beallitasok/naplo-audit" element={<AuditLogPage />} />
            <Route path="/beallitasok/bot-kulcsok" element={<BotApiKeysPage />} />
            <Route path="/beallitasok/ertesitesek" element={<NotificationPrefsPage />} />
            <Route path="/beallitasok/anomaliak" element={<AnomaliesPage />} />
            <Route path="/beallitasok/megosztas" element={<ShareLinksPage />} />
            <Route path="/beallitasok/ai" element={<AiOpsPage />} />

            {/* Legacy redirects — primary IA */}
            <Route path="/dashboard" element={<Navigate to="/ma" replace />} />
            <Route path="/expenses" element={<Navigate to="/penz/kiadasok" replace />} />
            <Route path="/quotes" element={<Navigate to="/penz/ajanlatok" replace />} />
            <Route path="/jovahagyas" element={<ApprovalsPage />} />
            <Route
              path="/projekt-ai"
              element={<Navigate to="/asszisztens?mod=kerdezz" replace />}
            />
            <Route path="/dontesek" element={<Navigate to="/asszisztens" replace />} />
            <Route path="/documents" element={<Navigate to="/dokumentumok" replace />} />
            <Route path="/naplo" element={<Navigate to="/dokumentumok?tipus=naplo" replace />} />
            <Route path="/utemterv" element={<Navigate to="/munka?nezet=utem" replace />} />

            {/* A) cut — soft-gone */}
            <Route
              path="/ajanlatok/rfq"
              element={<Navigate to="/penz/rfq" replace />}
            />
            <Route
              path="/ajanlatok/osszehasonlitas/:variantGroup"
              element={<CompareRedirect />}
            />
            <Route path="/cashflow" element={<Navigate to="/penz/cashflow" replace />} />
            <Route
              path="/eszkozigeny"
              element={<GoneTo to="/munka" label="Eszközigény a feladatnál" />}
            />
            <Route path="/cikkek/atvetelek" element={<Navigate to="/atvetelek" replace />} />

            {/* Régi oldalak: a mai útvonalakra visznek, a lista nem linkel ide. */}
            <Route path="/_legacy/dashboard" element={<Navigate to="/beallitasok" replace />} />
            <Route path="/_legacy/phases" element={<Navigate to="/munka" replace />} />
            <Route path="/_legacy/approvals" element={<Navigate to="/jovahagyas" replace />} />
            <Route path="/_legacy/ai" element={<Navigate to="/asszisztens" replace />} />
            <Route path="/_legacy/schedule" element={<Navigate to="/munka?nezet=utem" replace />} />
            <Route path="/_legacy/documents" element={<Navigate to="/dokumentumok" replace />} />
            <Route
              path="/_legacy/naplo"
              element={<Navigate to="/dokumentumok?tipus=naplo" replace />}
            />

            {/* Alias: old quote detail under ajanlatok still works via QuoteDetailPage above */}
            <Route path="/quotes/:id" element={<QuoteDetailRedirect />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/ma" replace />} />
      </Routes>
    </React.Suspense>
  );
}

function RequireAuth() {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-dvh bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50">
        <div className="mx-auto flex max-w-md items-center justify-center px-4 py-10">
          <LoadingSpinner />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}
